import React, { useCallback } from "react";
import { Button, Modal, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseSquareOutlined,
} from "@ant-design/icons";
import ee from "services/EventEmitter";
import { MODAL } from "constants/translates";
import {
  DELETE_LOGISTIC_EVENT,
  REMOVE_EDITING_KEY,
  SAVE_LOGISTIC_ENTITY,
  SET_EDITING_KEY,
  UPDATE_LOGISTIC_ENTITY,
} from "constants/events";
import LogisticStore from "stores/LogisticStore";
import { observer } from "mobx-react";
import { Moment } from "moment";
import { Logistic } from "models/Logistic";
import { UploadFile } from "antd/lib/upload/interface";

interface Props {
  entity: Logistic;
  isTemporary?: boolean;
  form?: any;
}

interface FileProps {
  file: UploadFile;
  fileList: UploadFile[];
}

const Actions: React.FC<Props> = ({ entity, form, isTemporary }) => {
  const entityId = entity?.id;
  const { editingKeys } = LogisticStore;
  const editMode = editingKeys?.some((key) => key === entityId);

  const onDeleteHandler = useCallback(() => {
    Modal.confirm({
      title: MODAL.confirm_title,
      content: MODAL.confirm_delete,
      onOk: () => {
        ee.emit(DELETE_LOGISTIC_EVENT, {
          entityId,
          isTemporary,
          children: entity?.children,
        });
      },
    });
  }, [entityId]);

  const setEditMode = () => {
    ee.emit(SET_EDITING_KEY, entityId);
  };

  const saveRow = async () => {
    const row = await form.validateFields();

    const savingKeys = Object.keys(row).filter((current) => {
      return current.includes(`_${entityId}`);
    });

    const rowData = Object.fromEntries(
      Object.entries(row).filter((m) => savingKeys.includes(m[0]))
    );

    Object.keys(rowData).forEach(function (key) {
      const newKey = key?.replace(`_${entityId}`, "");
      if (rowData) {
        Object.defineProperty(
          rowData,
          newKey,
          Object.getOwnPropertyDescriptor(rowData, key) || ""
        );

        delete rowData[key];
      }
    });

    const rowAttachedData = rowData?.documents as FileProps;
    const rowFiles = rowAttachedData?.fileList?.map(
      (file: UploadFile) => file.originFileObj
    );

    const savingData = {
      ...rowData,
      loading_date:
        (rowData.loading_date as Moment)?.format("YYYY-MM-DD") || "",
      payment_date:
        (rowData.payment_date as Moment)?.format("YYYY-MM-DD") || "",
      docs_sent_date: rowData.docs_sent_date
        ? (rowData.docs_sent_date as Moment)?.format("YYYY-MM-DD")
        : "",
      parent_id: entity?.parent_id,
      documents: entity?.documents,
    };

    const dateAction = entity.temporary
      ? SAVE_LOGISTIC_ENTITY
      : UPDATE_LOGISTIC_ENTITY;

    ee.emit(dateAction, { savingData, entityId, documents: rowFiles });
  };

  const cancelEdit = async () => {
    ee.emit(REMOVE_EDITING_KEY, entityId);
  };

  return (
    <>
      {editMode ? (
        <Tooltip placement="top" title="Save row">
          <Button type="primary" onClick={saveRow}>
            <SaveOutlined style={{ fontSize: "25px", paddingTop: "4px" }} />
          </Button>
        </Tooltip>
      ) : (
        <Tooltip placement="top" title="Edit row">
          <Button type="primary" onClick={setEditMode}>
            <EditOutlined />
          </Button>
        </Tooltip>
      )}
      <Tooltip placement="top" title="Delete row">
        <Button type="primary" onClick={onDeleteHandler}>
          <DeleteOutlined />
        </Button>
      </Tooltip>
      {editMode && (
        <>
          &nbsp;&nbsp;
          <Tooltip placement="top" title="Cancel changes">
            <Button type="primary" onClick={cancelEdit}>
              <CloseSquareOutlined />
            </Button>
          </Tooltip>
        </>
      )}
    </>
  );
};

export default observer(Actions);
